import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@wilco/api";
import { Badge, Button, Dialog, Notice, Plate, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { useCan } from "../auth.ts";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { formatDate, formatMoney, formatNumber, t } from "../i18n.ts";
import { productInfos } from "../lookups.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { costingApi, formatMonth, monthOf, validMonth, type Close } from "./api.ts";
import { CostingHead, MonthPicker } from "./CostingNav.tsx";

const route = getRouteApi("/app/costing/close");
const COSTING = ["costing"] as const;

/**
 * Закрытие месяца (docs/acceptance/month-close.md): ФОТ официальной и
 * наличной частью, доли SER, RND, SRV, база серии по коэффициенту или по
 * минутам «Час складання» с предложением коэффициентов из минут, затем
 * «Закрити місяць» одной транзакцией с блокировкой периода (ЗМ-01).
 * Повторно открыть можно только последний закрытый месяц и с причиной (H4,
 * ЗМ-27, ЗМ-28). Кнопки по правам, проверяет сервер (ЗМ-23).
 */
export function MonthClosePage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const month = validMonth(search.month, monthOf(-1));
  const setMonth = (m: string) => void navigate({ to: "/costing/close", search: { month: m }, replace: true });
  const settings = useQuery({ queryKey: [...COSTING, "settings"], queryFn: costingApi.settings });
  const close = useQuery({ queryKey: [...COSTING, "close", month], queryFn: () => costingApi.close(month), retry: false });
  const missing = close.error instanceof ApiError && close.error.status === 404;

  return (
    <div className="page costing">
      <CostingHead title={t("costing.title")} />
      <div className="costing-bar">
        <MonthPicker month={month} onChange={setMonth} />
        {settings.data ? (
          <span className="w-muted">
            {[
              t("costing.firstMonth", { month: formatMonth(settings.data.firstMonth) }),
              settings.data.lockDate ? t("costing.lockDate", { date: formatDate(settings.data.lockDate) }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        ) : null}
      </div>
      {close.error && !missing ? <Notice tone="danger" title={errorNotice(close.error).title} className="stock-notice" /> : null}
      {missing ? <NoDraft month={month} /> : null}
      {close.data ? <CloseView key={month} c={close.data} /> : null}
    </div>
  );
}

function NoDraft({ month }: { month: string }) {
  const can = useCan();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const create = useMutation({ mutationFn: () => costingApi.draft(month) });
  return (
    <Notice tone="info" title={t("costing.noDraft", { month: formatMonth(month) })} className="stock-notice">
      {notice ? <p>{notice.title}</p> : null}
      {can("costing.close") ? (
        <div className="notice-actions">
          <Button
            variant="primary"
            disabled={create.isPending}
            onClick={() =>
              create.mutate(undefined, {
                onSuccess: (c) => queryClient.setQueryData([...COSTING, "close", month], c),
                onError: (e) => setNotice(errorNotice(e)),
              })
            }
          >
            {t("costing.createDraft")}
          </Button>
        </div>
      ) : null}
    </Notice>
  );
}

const num = (v: number | null | undefined) => (v == null ? "" : String(v));

function CloseView({ c }: { c: Close }) {
  const can = useCan();
  const queryClient = useQueryClient();
  const m = c.month;
  const draft = c.state === "DRAFT";
  const edit = draft && can("costing.close");
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [reopen, setReopen] = useState(false);
  const run = useMutation({ mutationFn: (fn: () => Promise<Close | null>) => fn() });
  const act = (fn: () => Promise<Close | null>, ok?: NoticeState) =>
    run.mutate(fn, {
      onSuccess: (next) => {
        if (next) queryClient.setQueryData([...COSTING, "close", m], next);
        void queryClient.invalidateQueries({ queryKey: COSTING });
        setNotice(ok ?? null);
      },
      onError: (e) => setNotice(errorNotice(e)),
    });

  const [official, setOfficial] = useState(num(c.official));
  const [cash, setCash] = useState(num(c.cash));
  const [ser, setSer] = useState(num(c.ser));
  const [rnd, setRnd] = useState(num(c.rnd));
  const [srv, setSrv] = useState(num(c.srv));
  const sharesSum = (parseQty(ser) ?? 0) + (parseQty(rnd) ?? 0) + (parseQty(srv) ?? 0);

  const savePayroll = (e: SubmitEvent) => {
    e.preventDefault();
    act(() => costingApi.payroll(m, parseQty(official) ?? -1, parseQty(cash) ?? -1), { tone: "success", title: t("costing.saved") });
  };
  const saveShares = (e: SubmitEvent) => {
    e.preventDefault();
    act(() => costingApi.shares(m, parseQty(ser) ?? -1, parseQty(rnd) ?? -1, parseQty(srv) ?? -1), { tone: "success", title: t("costing.saved") });
  };

  return (
    <>
      {notice ? <Notice tone={notice.tone} title={notice.title} className={notice.pop ? "stock-notice stock-pop" : "stock-notice"} /> : null}
      <Plate cut="lg" className="order-head">
        <div className="costing-state">
          <span className="costing-name">{c.name}</span>
          <Badge tone={draft ? "neutral" : "success"}>{t(draft ? "costing.state.DRAFT" : "costing.state.CLOSED")}</Badge>
          {c.closedOn ? <span className="w-muted">{t("costing.closedOn", { date: formatDate(c.closedOn) })}</span> : null}
        </div>
      </Plate>

      <div className="costing-grid">
        <section className="stock-section" aria-labelledby="close-payroll">
          <h2 id="close-payroll" className="desk-group-title">
            {t("costing.payroll")}
          </h2>
          {edit ? (
            <form className="stock-form" onSubmit={savePayroll}>
              <TextField label={t("costing.official")} hint={t("costing.officialHint")} inputMode="decimal" value={official} onChange={(e) => setOfficial(e.target.value)} />
              <TextField label={t("costing.cash")} inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} />
              <Button type="submit" variant="secondary" disabled={run.isPending}>
                {t("costing.save")}
              </Button>
            </form>
          ) : (
            <dl className="cost-sum">
              <Row label={t("costing.official")} value={c.official == null ? "—" : formatMoney(c.official, "UAH")} />
              <Row label={t("costing.cash")} value={c.cash == null ? "—" : formatMoney(c.cash, "UAH")} />
            </dl>
          )}
        </section>

        <section className="stock-section" aria-labelledby="close-shares">
          <h2 id="close-shares" className="desk-group-title">
            {t("costing.shares")}
          </h2>
          {edit ? (
            <form className="stock-form costing-shares" onSubmit={saveShares}>
              <TextField label="SER, %" inputMode="decimal" value={ser} onChange={(e) => setSer(e.target.value)} />
              <TextField label="RND, %" inputMode="decimal" value={rnd} onChange={(e) => setRnd(e.target.value)} />
              <TextField label="SRV, %" inputMode="decimal" value={srv} onChange={(e) => setSrv(e.target.value)} />
              <p className={sharesSum === 100 ? "w-muted" : "line-card-alert"}>{t("costing.sharesSum", { sum: formatNumber(sharesSum) })}</p>
              <Button type="submit" variant="secondary" disabled={run.isPending}>
                {t("costing.save")}
              </Button>
            </form>
          ) : (
            <dl className="cost-sum">
              <Row label="SER" value={`${formatNumber(c.ser)} %`} />
              <Row label="RND" value={`${formatNumber(c.rnd)} %`} />
              <Row label="SRV" value={`${formatNumber(c.srv)} %`} />
            </dl>
          )}
        </section>
      </div>

      {draft ? <Basis month={m} basis={c.basis} edit={edit} onPick={(b) => act(() => costingApi.setBasis(m, b))} pending={run.isPending} /> : null}

      {c.units.length > 0 ? (
        <section className="stock-section" aria-labelledby="close-units">
          <h2 id="close-units" className="desk-group-title">
            {t("costing.units")}
          </h2>
          <ul className="payments">
            {c.units.map((u) => (
              <li key={u.serial} className="payment">
                <span className="w-num">{u.serial}</span>
                <span className="payment-ref w-muted">{t(`costing.target.${u.target}` as "costing.target.STOCK")}</span>
                <span className="w-num payment-sum">{formatMoney(u.amount, "UAH")}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {c.repairs.length > 0 ? (
        <section className="stock-section" aria-labelledby="close-repairs">
          <h2 id="close-repairs" className="desk-group-title">
            {t("costing.repairs")}
          </h2>
          <ul className="payments">
            {c.repairs.map((r) => (
              <li key={r.repair} className="payment">
                <span className="w-num">{r.repair}</span>
                <span className="payment-ref w-muted">{`${r.family} · ${t("unit.minutes", { minutes: formatNumber(r.minutes) })}`}</span>
                <span className="w-num payment-sum">{formatMoney(r.amount, "UAH")}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Notes c={c} onAdd={(text) => act(() => costingApi.note(m, text))} pending={run.isPending} />

      {c.history.length > 0 ? (
        <section className="stock-section" aria-labelledby="close-history">
          <h2 id="close-history" className="desk-group-title">
            {t("costing.history")}
          </h2>
          <ul className="costing-history w-muted">
            {c.history.map((h, i) => (
              <li key={`${String(i)}-${h}`}>{h}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="order-actions">
        {edit ? (
          <Button
            variant="primary"
            disabled={run.isPending}
            onClick={() =>
              setConfirm({
                title: t("costing.closeMonth.title", { month: formatMonth(m) }),
                body: t("costing.closeMonth.body"),
                action: t("costing.closeMonth"),
                run: () => act(() => costingApi.closeMonth(m), { tone: "success", title: t("costing.closed", { month: formatMonth(m) }), pop: true }),
              })
            }
          >
            {t("costing.closeMonth")}
          </Button>
        ) : null}
        {edit ? (
          <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => costingApi.deleteDraft(m).then(() => null))}>
            {t("costing.deleteDraft")}
          </Button>
        ) : null}
        {!draft && can("costing.reopen") ? (
          <Button variant="ghost" onClick={() => setReopen(true)}>
            {t("costing.reopen")}
          </Button>
        ) : null}
      </div>

      {reopen ? (
        <ReopenDialog
          month={m}
          onClose={() => setReopen(false)}
          onDone={(next) => {
            setReopen(false);
            queryClient.setQueryData([...COSTING, "close", m], next);
            void queryClient.invalidateQueries({ queryKey: COSTING });
            setNotice({ tone: "success", title: t("costing.reopened", { month: formatMonth(m) }) });
          }}
        />
      ) : null}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="w-num">{value}</dd>
    </div>
  );
}

/**
 * База ФОТ серии (ЗМ-02): по коэффициенту трудоёмкости версии или по
 * минутам «Час складання». Рядом предложение коэффициентов из средних
 * минут: бухгалтер видит, насколько коэффициенты разошлись с цехом.
 */
function Basis({ month, basis, edit, onPick, pending }: { month: string; basis: string; edit: boolean; onPick: (b: "COEFFICIENT" | "MINUTES") => void; pending: boolean }) {
  const preview = useQuery({ queryKey: [...COSTING, "basis", month, basis], queryFn: () => costingApi.basis(month) });
  const p = preview.data;
  const names = useQuery({
    queryKey: ["lookups", "products", ...(p?.versions.map((v) => v.product) ?? [])],
    queryFn: () => productInfos([...(p?.versions.map((v) => v.product) ?? []), ...(p?.units.map((u) => u.product) ?? [])]),
    enabled: Boolean(p),
  });
  const name = (id: string) => names.data?.get(id)?.name ?? id;
  return (
    <section className="stock-section" aria-labelledby="close-basis">
      <h2 id="close-basis" className="desk-group-title">
        {t("costing.basis")}
      </h2>
      {preview.error ? <Notice tone="danger" title={errorNotice(preview.error).title} /> : null}
      <div className="costing-basis-pick" role="radiogroup" aria-label={t("costing.basis")}>
        {(["COEFFICIENT", "MINUTES"] as const).map((b) => (
          <label key={b} className="check">
            <input type="radio" name="basis" checked={basis === b} disabled={!edit || pending} onChange={() => onPick(b)} />
            {t(b === "COEFFICIENT" ? "costing.basis.COEFFICIENT" : "costing.basis.MINUTES")}
          </label>
        ))}
      </div>
      {p ? (
        <>
          <p className="w-muted">{t("costing.pool", { sum: formatMoney(p.pool, "UAH") })}</p>
          {p.missingMinutes.length > 0 ? <Notice tone="warning" title={t("costing.missingMinutes", { serials: p.missingMinutes.join(", ") })} /> : null}
          {p.versions.length > 0 ? (
            <div className="costing-table" role="table" aria-label={t("costing.versions")}>
              <div role="row" data-head="">
                <span role="columnheader">{t("costing.version")}</span>
                <span role="columnheader" className="num">
                  {t("costing.avgMinutes")}
                </span>
                <span role="columnheader" className="num">
                  {t("costing.proposed")}
                </span>
              </div>
              {p.versions.map((v) => (
                <div role="row" key={`${v.product}-${v.version ?? ""}`}>
                  <span role="cell">{name(v.product)}</span>
                  <span role="cell" className="num w-num">
                    {formatNumber(v.averageMinutes)}
                  </span>
                  <span role="cell" className="num w-num">
                    {formatNumber(v.proposedCoefficient)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
          {p.units.length > 0 ? (
            <div className="costing-table" role="table" aria-label={t("costing.unitsBasis")} data-cols="5">
              <div role="row" data-head="">
                <span role="columnheader">{t("costing.serial")}</span>
                <span role="columnheader" className="num">
                  {t("costing.coefficient")}
                </span>
                <span role="columnheader" className="num">
                  {t("costing.minutes")}
                </span>
                <span role="columnheader" className="num">
                  {t("costing.byCoefficient")}
                </span>
                <span role="columnheader" className="num">
                  {t("costing.byMinutes")}
                </span>
              </div>
              {p.units.map((u) => (
                <div role="row" key={u.serial}>
                  <span role="cell" className="w-num">
                    {u.serial}
                  </span>
                  <span role="cell" className="num w-num">
                    {formatNumber(u.coefficient)}
                  </span>
                  <span role="cell" className="num w-num">
                    {u.minutes == null ? "—" : formatNumber(u.minutes)}
                  </span>
                  <span role="cell" className="num w-num">
                    {u.byCoefficient == null ? "—" : formatMoney(u.byCoefficient, "UAH")}
                  </span>
                  <span role="cell" className="num w-num">
                    {u.byMinutes == null ? "—" : formatMoney(u.byMinutes, "UAH")}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** Примечания, в том числе к закрытому месяцу (CST-04). */
function Notes({ c, onAdd, pending }: { c: Close; onAdd: (text: string) => void; pending: boolean }) {
  const can = useCan();
  const [text, setText] = useState("");
  return (
    <section className="stock-section" aria-labelledby="close-notes">
      <h2 id="close-notes" className="desk-group-title">
        {t("costing.notes")}
      </h2>
      {c.notes.length === 0 ? <p className="w-muted">{t("costing.noNotes")}</p> : null}
      <ul className="costing-history">
        {c.notes.map((n, i) => (
          <li key={`${String(i)}-${n}`}>{n}</li>
        ))}
      </ul>
      {can("costing.close") ? (
        <form
          className="qty-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            onAdd(text.trim());
            setText("");
          }}
        >
          <TextField label={t("costing.note")} hideLabel placeholder={t("costing.note")} value={text} onChange={(e) => setText(e.target.value)} />
          <Button type="submit" variant="secondary" disabled={pending || !text.trim()}>
            {t("costing.addNote")}
          </Button>
        </form>
      ) : null}
    </section>
  );
}

/** «Відкрити заново» (H4): закрытие откатывается целиком, причина и автор в аудит. */
function ReopenDialog({ month, onClose, onDone }: { month: string; onClose: () => void; onDone: (c: Close) => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<NoticeState | null>(null);
  const reopen = useMutation({ mutationFn: () => costingApi.reopen(month, reason.trim()) });
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t("costing.reopen.title", { month: formatMonth(month) })}
      description={t("costing.reopen.body")}
      footer={
        <Button variant="primary" disabled={reopen.isPending || !reason.trim()} onClick={() => reopen.mutate(undefined, { onSuccess: onDone, onError: (e) => setError(errorNotice(e)) })}>
          {t("costing.reopen")}
        </Button>
      }
    >
      {error ? <Notice tone="danger" title={error.title} /> : null}
      <TextField label={t("costing.reopen.reason")} value={reason} onChange={(e) => setReason(e.target.value)} required />
    </Dialog>
  );
}
